/** @jsxImportSource davaux/client */
import { createEffect, createSignal } from 'davaux/client'

type ColorName =
  | 'blue'
  | 'cyan'
  | 'gray'
  | 'green'
  | 'orange'
  | 'red'
  | 'teal'
  | 'violet'
  | 'yellow'

const SCHEMES: Array<{ name: ColorName; label: string }> = [
  { name: 'blue', label: 'Blue' },
  { name: 'violet', label: 'Violet' },
  { name: 'green', label: 'Green' },
  { name: 'red', label: 'Red' },
  { name: 'orange', label: 'Orange' },
  { name: 'yellow', label: 'Yellow' },
  { name: 'teal', label: 'Teal' },
  { name: 'cyan', label: 'Cyan' },
  { name: 'gray', label: 'Gray' },
]

function injectScheme(name: string) {
  if (typeof document === 'undefined') return
  let el = document.getElementById('dv-scheme-override') as HTMLStyleElement | null
  if (!el) {
    el = document.createElement('style')
    el.id = 'dv-scheme-override'
    document.head.appendChild(el)
  }
  el.textContent = [
    ':root {',
    `  --dv-color-primary-filled: var(--dv-color-${name}-filled);`,
    `  --dv-color-primary-filled-hover: var(--dv-color-${name}-filled-hover);`,
    `  --dv-color-primary-filled-color: var(--dv-color-${name}-filled-color);`,
    `  --dv-color-primary-light: var(--dv-color-${name}-light);`,
    `  --dv-color-primary-light-hover: var(--dv-color-${name}-light-hover);`,
    `  --dv-color-primary-light-color: var(--dv-color-${name}-light-color);`,
    `  --dv-color-primary-outline: var(--dv-color-${name}-outline);`,
    `  --dv-color-primary-outline-hover: var(--dv-color-${name}-outline-hover);`,
    `  --dv-color-primary-outline-color: var(--dv-color-${name}-outline-color);`,
    '}',
  ].join('\n')
}

function generateCSS(name: string): string {
  return [
    ':root {',
    `  --dv-color-primary-filled: var(--dv-color-${name}-filled);`,
    `  --dv-color-primary-filled-hover: var(--dv-color-${name}-filled-hover);`,
    `  --dv-color-primary-filled-color: var(--dv-color-${name}-filled-color);`,
    `  --dv-color-primary-light: var(--dv-color-${name}-light);`,
    `  --dv-color-primary-light-hover: var(--dv-color-${name}-light-hover);`,
    `  --dv-color-primary-light-color: var(--dv-color-${name}-light-color);`,
    `  --dv-color-primary-outline: var(--dv-color-${name}-outline);`,
    `  --dv-color-primary-outline-hover: var(--dv-color-${name}-outline-hover);`,
    `  --dv-color-primary-outline-color: var(--dv-color-${name}-outline-color);`,
    '}',
  ].join('\n')
}

export default function ColorSchemePicker({ value }: { value?: string }) {
  const stored =
    typeof localStorage !== 'undefined' ? (localStorage.getItem('dv-scheme') ?? '') : ''
  const [selected, setSelected] = createSignal(value ?? stored)

  createEffect(() => {
    const scheme = selected()
    if (!scheme) return
    injectScheme(scheme)
  })

  function handleSelect(name: ColorName) {
    setSelected(name)
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('dv-scheme', name)
    }
  }

  function handleCopy() {
    const scheme = selected()
    if (!scheme || typeof navigator === 'undefined') return
    navigator.clipboard.writeText(generateCSS(scheme)).catch(() => {})
  }

  return (
    <div class='dv-color-scheme-picker'>
      {SCHEMES.map(({ name, label }) => (
        <button
          type='button'
          class='dv-scheme-swatch'
          data-active={() => (selected() === name ? '' : undefined)}
          style={`--dv-swatch-color: var(--dv-color-${name}-filled)`}
          title={label}
          aria-label={label}
          aria-pressed={() => selected() === name}
          onClick={() => handleSelect(name)}
        />
      ))}
      <button
        type='button'
        class='dv-button'
        data-variant='default'
        data-size='xs'
        onClick={handleCopy}
        title='Copy primary color CSS to clipboard'
        style='margin-left: var(--dv-spacing-xs)'
      >
        <span class='dv-button-inner'>
          <span class='dv-button-label'>Copy CSS</span>
        </span>
      </button>
    </div>
  )
}
